import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { Unsubscribe } from 'router5/dist/types/base';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { RADIX_PARAM } from '../../constants/common.constants';
import { ZoomUsersManagementViewModel } from './_zoom-users-management/zoom-users-management.viewmodel';
import { TrackedUsersViewModel } from './_tracked-users-list/tracked-users.viewmodel';
import { ScheduledMeetingsViewModel } from './_zoom-meetings/_scheduled-zoom-meetings/scheduled-zoom-meetings.viewmodel';
import { ScheduleNewMeetingViewModel } from './_zoom-meetings/_schedule-new-meeting/schedule-meeting.viewmodel';
import {
    ScheduledMeetingDetailsViewModel
} from './_zoom-meetings/_scheduled-zoom-meetings/_scheduled-zoom-meeting-details/scheduled-zoom-meeting-details.viewmodel';
import {
    ScheduledMeetingWebinarDetailsViewModel
} from './_zoom-meetings/_scheduled-zoom-meetings/_scheduled-zoom-meeting-webinar-details/scheduled-zoom-meeting-webinar-details.viewmodel';
import { UpdateMeetingViewModel } from './_zoom-meetings/_update-meeting/update-meeting.viewmodel';
import { TelephoneNumberSetsViewModel } from './_telephone-number-sets-list/telephone-number-sets.viewmodel';

enum ZoomPages {
    TrackedUsers,
    UsersManagement,
    ScheduledMeetings,
    ScheduledMeetingDetails,
    ScheduleNewMeeting,
    UpdateMeeting,
    CloneMeeting,
    ScheduledMeetingWebinarDetails,
    TelephoneNumberSets
}

class ZoomViewModel implements IModuleVM {
    @mobx.observable
    private currentPage: ZoomPages;

    @mobx.observable
    private meetingId: number;

    private subscriptions: Unsubscribe[];

    constructor(
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly trackedUsersViewModel: TrackedUsersViewModel = IoC.Container.get(TrackedUsersViewModel),
        @IoC.Inject private readonly usersManagementViewModel: ZoomUsersManagementViewModel = IoC.Container.get(ZoomUsersManagementViewModel),
        @IoC.Inject private readonly scheduledMeetingsViewModel: ScheduledMeetingsViewModel = IoC.Container.get(ScheduledMeetingsViewModel),
        @IoC.Inject private readonly scheduledMeetingDetailsViewModel: ScheduledMeetingDetailsViewModel = IoC.Container.get(ScheduledMeetingDetailsViewModel),
        @IoC.Inject private readonly scheduledMeetingWebinarDetailsViewModel: ScheduledMeetingWebinarDetailsViewModel =
            IoC.Container.get(ScheduledMeetingWebinarDetailsViewModel),
        @IoC.Inject private readonly scheduleNewMeetingViewModel: ScheduleNewMeetingViewModel = IoC.Container.get(ScheduleNewMeetingViewModel),
        @IoC.Inject private readonly updateMeetingViewModel: UpdateMeetingViewModel = IoC.Container.get(UpdateMeetingViewModel),
        @IoC.Inject private readonly telephoneNumberSetsViewModel: TelephoneNumberSetsViewModel = IoC.Container.get(TelephoneNumberSetsViewModel),
        ) {
        this.subscriptions = [];
    }

    @mobx.action
    public didMount() {
        this.startListeningRouter();
        this.trackedUsersViewModel.setupRouterListeners();
    }

    public getCurrentPage() {
        return this.currentPage;
    }

    public getUsersManagementVM() {
        return this.usersManagementViewModel;
    }

    public getTrackedUserVM() {
        return this.trackedUsersViewModel;
    }

    public getScheduledMeetingsVM() {
        return this.scheduledMeetingsViewModel;
    }

    public getScheduledMeetingDetailsVM() {
        return this.scheduledMeetingDetailsViewModel;
    }

    public getScheduledMeetingWebinarDetailsVM() {
        return this.scheduledMeetingWebinarDetailsViewModel;
    }

    public getScheduleNewMeetingVM() {
        return this.scheduleNewMeetingViewModel;
    }

    public getUpdateMeetingVM() {
        return this.updateMeetingViewModel;
    }

    public getMeetingId() {
        return this.meetingId;
    }

    public getTelephoneNumberSetsVM() {
        return this.telephoneNumberSetsViewModel;
    }

    @mobx.action
    public willUnmount() {
        this.subscriptions.forEach(x => x());
        this.trackedUsersViewModel.cleanup();
        this.scheduledMeetingsViewModel.cleanup();
        this.telephoneNumberSetsViewModel.cleanup();
    }

    private startListeningRouter() {
        this.subscriptions.push(
            this.router.listen(Routes.ZoomUsersManagement, () => {
                mobx.runInAction(async () => {
                    this.currentPage = ZoomPages.UsersManagement;
                    this.usersManagementViewModel.resetPagingVM();
                    this.usersManagementViewModel.resetSelection();
                    this.trackedUsersViewModel.cleanup();
                    await this.usersManagementViewModel.fetchUsersPage();
                });
            })
        );

        this.subscriptions.push(
            this.router.listen(Routes.TrackedZoomUsers, () => {
                mobx.runInAction(async () => {
                    this.currentPage = ZoomPages.TrackedUsers;
                    await this.trackedUsersViewModel.fetchTrackedUsers();
                });
            })
        );

        this.subscriptions.push(
            this.router.listen(Routes.TelephoneNumberSets, () => {
                mobx.runInAction(async () => {
                    this.currentPage = ZoomPages.TelephoneNumberSets;
                    await this.telephoneNumberSetsViewModel.fetchTelephoneNumberSets();
                });
            })
        );

        this.subscriptions.push(
            this.router.listen(
                Routes.ZoomScheduledMeetings,
                () => {
                    mobx.runInAction(async () => {
                        this.currentPage = ZoomPages.ScheduledMeetings;
                        await this.scheduledMeetingsViewModel.fetchScheduledMeetings();
                    });
                },
                () => this.scheduledMeetingsViewModel.cleanRecources()
            )
        );

        this.subscriptions.push(
            this.router.listen(Routes.ZoomScheduledMeetingDetails, () => {
                mobx.runInAction(async () => {
                    this.currentPage = ZoomPages.ScheduledMeetingDetails;
                });
            })
        );

        this.subscriptions.push(
            this.router.listen(Routes.ZoomScheduledMeetingWebinarDetails, () => {
                mobx.runInAction(async () => {
                    this.currentPage = ZoomPages.ScheduledMeetingWebinarDetails;
                });
            })
        );

        this.subscriptions.push(
            this.router.listen(
                Routes.ZoomScheduleNewMeeting, routerArgs => {
                    mobx.runInAction(async () => {
                        this.currentPage = ZoomPages.ScheduleNewMeeting;
                        this.meetingId = parseInt(routerArgs.clonedMeetingId, RADIX_PARAM)
                            ?? this.scheduledMeetingDetailsViewModel.getCurrentMeetingId();
                    });
                },
                () => this.scheduleNewMeetingViewModel.cleanRecources()
            )
        );
        this.subscriptions.push(
            this.router.listen(
                Routes.ZoomUpdateMeeting, routerArgs => {
                    mobx.runInAction(async () => {
                        this.currentPage = ZoomPages.UpdateMeeting;
                        this.meetingId = parseInt(routerArgs.meetingId, RADIX_PARAM)
                            ?? this.scheduledMeetingDetailsViewModel.getCurrentMeetingId();
                    });
                })
        );
        this.subscriptions.push(
          this.router.listenOthers(
            Routes.TrackedZoomUsers, () => {
                this.trackedUsersViewModel.cleanup();
            }
          )
        );
    }
}

export { ZoomViewModel, ZoomPages };
